import { Link } from "react-router-dom";
import {
    Diamond, Gem, Trophy, FlaskConical, Atom, ArrowRight, ArrowUpRight,
    Sparkles, Smile, Microscope, Cpu, Building2, Award, Scan, Layers, ShieldCheck, Rocket
} from "lucide-react";
import { BrandImage } from "@/lib/brand";

/* ============================================================== */
/*   PIONEERING THE FUTURE OF DENTAL NANOTECHNOLOGY                */
/* ============================================================== */

const ECOSYSTEM = [
    {
        icon: Microscope,
        title: "First-to-Test Validation",
        body: "Before a new nanomaterial reaches the broader dental market, it is rigorously vetted in our labs. We act as the vanguard for emerging nanotechnology — running exhaustive structural, biocompatibility, and accelerated-wear screens to surface materials with real potential to revolutionize dental care and protection.",
        kpi: "12-month rolling material trials",
    },
    {
        icon: Cpu,
        title: "Purpose-Driven Invention",
        body: "We don't just test materials — we transform them. Our engineering and dental science teams collaborate to invent proprietary products from the ground up. Leveraging nano-scale properties — unprecedented tensile strength, ultra-light form factors, advanced shock absorption — we ship solutions that simply weren't possible before.",
        kpi: "6 active in-house IP programs",
    },
    {
        icon: Rocket,
        title: "Scaled Volume Commercialization",
        body: "Innovation means little if it cannot reach the people who need it. Toothrocket is the first to take groundbreaking nanotech dental products out of the laboratory and commercialize them at scale. Streamlined manufacturing brings high-volume, next-generation dental technologies to the global market faster than anyone else.",
        kpi: "GCC + India · scaled fabrication",
    },
];

const PRINTER_PARTNERS = [
    {
        name: "Asiga",
        slug: "rnd_partner_asiga",
        role: "Gold Standard · DLP Validation",
        status: "Active Testing",
        body: "Asiga Max & Pro 4K printers form the calibration backbone of our printed-resin pipeline. Every nano-ceramic hybrid we ship is mapped against an Asiga reference build.",
        img: "https://images.pexels.com/photos/8438916/pexels-photo-8438916.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
        accent: "from-amber-50 to-white",
    },
    {
        name: "Ketprint",
        slug: "rnd_partner_ketprint",
        role: "Materials & Workflow Partner",
        status: "Pilot Partnership",
        body: "Joint validation of high-throughput dental resins at our Doha lab — refining cure profiles, surface finish, and clinical longevity for crown-and-bridge workflows.",
        img: "https://images.pexels.com/photos/4480505/pexels-photo-4480505.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
        accent: "from-cyan-50 to-white",
    },
    {
        name: "BEGO",
        slug: "rnd_partner_bego",
        role: "Surgical-Guide R&D",
        status: "Co-Development",
        body: "Co-developing nano-reinforced surgical guides on BEGO's Varseo XL platform — pushing tolerances below 50 µm for fully-guided implantology.",
        img: "https://images.pexels.com/photos/3845126/pexels-photo-3845126.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
        accent: "from-teal-50 to-white",
    },
];

const PROJECTS = [
    {
        title: "Snap-Fit Veneers · The Instant Smile Solution",
        slug: "rnd_project_sf01",
        eyebrow: "· Project SF-01 · Launching Q4 2026",
        body: "A custom 3D-printed dental-resin arch that slides over the existing teeth — a 'beauty mask' for the smile. Non-invasive, reversible, and far more affordable than porcelain. Ideal for milestone occasions, on-camera moments, dental-anxiety patients, and anyone who wants to test-drive a smile before going permanent.",
        bullets: [
            "Zero enamel reduction · Fully removable",
            "Digital impressions → lab fabrication → snap-on fitting",
            "1–5 year service life · Dental-grade printed resin",
            "Full-arch makeover for less than a single porcelain veneer",
        ],
        launchBadge: "Q4 2026",
        icon: Smile,
        accent: "from-cyan-50 to-white",
        img: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=900&q=80",
    },
    {
        title: "Diamond-Embedded Slip-on Veneers",
        slug: "rnd_project_d04",
        eyebrow: "· Project D-04",
        body: "Lab-grown micro-diamond inclusions seated into the surface enamel-layer of a removable nano-ceramic veneer shell. Snap-on retention, no enamel reduction.",
        bullets: ["0.3 mm hybrid laminate base", "Pressure-fitted micro-diamonds", "Reversible · No enamel prep"],
        icon: Diamond,
        accent: "from-slate-100 to-white",
        img: "https://images.unsplash.com/photo-1771442873035-474765b40ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Dental Jewellery · Swarovski Inset",
        slug: "rnd_project_j09",
        eyebrow: "· Project J-09",
        body: "Precision-set decorative crystals bonded to printed micro-mounts. Mounts are radiopaque, repositionable, and remove cleanly with no enamel insult.",
        bullets: ["Swarovski crystal range", "Single-visit application", "Removable mount geometry"],
        icon: Gem,
        accent: "from-cyan-50 to-white",
        img: "https://images.unsplash.com/photo-1616996690916-a4d9af24295d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Memory-Shape Hard Retainers",
        slug: "rnd_project_m12",
        eyebrow: "· Project M-12",
        body: "Shape-memory polymer matrix that returns to its programmed geometry at body temperature. Optionally embedded with patient-chosen jewellery accents.",
        bullets: ["Thermo-reactive matrix", "Programmable arch form", "Jewellery embedded options"],
        icon: Atom,
        accent: "from-teal-50 to-white",
        img: "https://images.unsplash.com/photo-1776406987595-ba14f3510c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Custom Sports Guards · Team Programs",
        slug: "rnd_project_s17",
        eyebrow: "· Project S-17",
        body: "Mouthguards tuned to the impact profile of the sport — boxing, rugby, hockey, MMA — printed in elastomer composites with team colours, logos, and chip-tagged player IDs.",
        bullets: ["Sport-specific impact tuning", "Team-color & logo printing", "RFID/NFC player chip optional"],
        icon: Trophy,
        accent: "from-amber-50 to-white",
        img: "https://images.pexels.com/photos/36101264/pexels-photo-36101264.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        title: "Applied Nano-Materials Lab",
        slug: "rnd_project_n01",
        eyebrow: "· Project N-01",
        body: "Continuous testing pipeline for every newly published nano-ceramic, photopolymer, and bio-active resin entering the dental market. Internal benchmarks, accelerated wear, biocompatibility screens.",
        bullets: ["12-month rolling material trials", "Accelerated wear & flex", "Published internal whitepapers"],
        icon: FlaskConical,
        accent: "from-cyan-100 to-white",
        img: "https://images.unsplash.com/photo-1771442873035-474765b40ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85",
    },
];

export default function RND() {
    return (
        <div data-testid="rnd-page" className="bg-white">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0B1120] text-white tr-grain">
                <div className="absolute -top-32 right-1/4 w-[520px] h-[520px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="tr-overline text-cyan-400 mb-4">· Research & Development</div>
                        <h1 className="font-display font-light text-5xl lg:text-7xl tracking-tight leading-[0.98]">
                            Pioneering the future of<br />
                            <span className="italic font-extralight text-cyan-300/90">dental nanotechnology.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-white/70 text-[15px] leading-[1.75]">
                            We don't just follow the latest advancements in dental science — we actively define them.
                            Toothrocket R&D is the industry's premier proving ground, ensuring the most advanced
                            nanotechnology materials in the world are tested, perfected, and deployed here first.
                        </p>
                    </div>
                </div>
            </section>

            {/* INTRO PARAGRAPH */}
            <section className="border-b border-slate-200">
                <div className="max-w-[1080px] mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
                    <p className="text-slate-700 text-[17px] lg:text-[19px] leading-[1.8] font-light max-w-3xl mx-auto">
                        At Toothrocket, we are committed to a cycle of <span className="italic text-slate-900">continuous, rapid innovation.</span> We
                        bridge the critical gap between theoretical material science and practical, everyday dental
                        solutions — printed, polished, and shipped to clinics in hours.
                    </p>
                </div>
            </section>

            {/* OUR R&D ECOSYSTEM — 3 PILLARS */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24" data-testid="rnd-ecosystem">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-7">
                        <div className="tr-overline mb-3">· The ecosystem</div>
                        <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                            Three engines, one mission.<br />
                            <span className="italic text-slate-500">Validate. Invent. Scale.</span>
                        </h2>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    {ECOSYSTEM.map((e, i) => {
                        const Icon = e.icon;
                        return (
                            <div
                                key={e.title}
                                data-testid={`rnd-ecosystem-pillar-${i}`}
                                className="group relative rounded-[24px] border border-slate-200 bg-white p-7 hover:border-cyan-400/60 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                            >
                                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 text-white flex items-center justify-center mb-6 shadow-md shadow-cyan-900/15">
                                    <Icon size={22} />
                                </div>
                                <div className="tr-overline text-slate-400 mb-2">· 0{i + 1}</div>
                                <h3 className="font-display font-medium text-2xl tracking-tight text-slate-900 leading-tight">{e.title}</h3>
                                <p className="mt-4 text-[14.5px] text-slate-600 leading-[1.75]">{e.body}</p>
                                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2 text-[12px] font-mono-tr tracking-[0.18em] uppercase text-cyan-700">
                                    <Sparkles size={13} /> {e.kpi}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* 3D PRINTING PARTNERSHIPS — ASIGA / KETPRINT / BEGO */}
            <section className="bg-[#0B1120] text-white tr-grain relative overflow-hidden" data-testid="rnd-printers">
                <div className="absolute -top-32 left-1/3 w-[520px] h-[520px] tr-glow bg-cyan-500/20" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                        <div className="lg:col-span-8">
                            <div className="tr-overline text-cyan-400 mb-3">· The printer floor</div>
                            <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                                Testing on every gold-standard<br />
                                <span className="italic font-extralight text-cyan-300/90">3D dental printer in production.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4">
                            <p className="text-white/65 text-[15px] leading-[1.75]">
                                Toothrocket R&D operates a multi-vendor printer floor — calibrated, monitored,
                                and benchmarked daily. Materials are never released to clinics until they pass
                                every machine in this rotation.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {PRINTER_PARTNERS.map((p) => (
                            <div
                                key={p.name}
                                data-testid={`rnd-printer-${p.name.toLowerCase()}`}
                                className="rounded-[24px] overflow-hidden bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:border-cyan-400/60 hover:bg-white/[0.06] transition-all duration-300"
                            >
                                <div className={`relative h-[220px] overflow-hidden bg-gradient-to-br ${p.accent}`}>
                                    <BrandImage slug={p.slug} fallback={p.img} alt={p.name} className="w-full h-full object-cover mix-blend-multiply opacity-90" />
                                    <div className="absolute top-4 right-4 px-2.5 py-1 rounded-md bg-cyan-500/95 text-white text-[10px] font-mono-tr tracking-[0.2em] uppercase shadow-lg">
                                        {p.status}
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-2 text-cyan-400 mb-2">
                                        <Scan size={14} />
                                        <span className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase">{p.role}</span>
                                    </div>
                                    <h3 className="font-display font-medium text-2xl tracking-tight text-white">{p.name}</h3>
                                    <p className="mt-3 text-[14px] text-white/65 leading-[1.7]">{p.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INCUBATION — QSTP + SIDRA MEDICINE */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24" data-testid="rnd-incubation">
                <div className="grid lg:grid-cols-12 gap-10 items-stretch">
                    {/* QSTP */}
                    <div className="lg:col-span-6">
                        <div className="h-full rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 lg:p-10 relative overflow-hidden">
                            <div className="absolute -bottom-20 -right-20 w-[280px] h-[280px] tr-glow bg-cyan-400/20" />
                            <div className="relative">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-700 font-mono-tr text-[10.5px] tracking-[0.22em] uppercase mb-6">
                                    <ShieldCheck size={13} /> · Active Incubation
                                </div>
                                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight text-slate-900 leading-tight">
                                    Incubated at <span className="font-medium">QSTP.</span>
                                </h3>
                                <p className="mt-4 text-slate-600 text-[15px] leading-[1.75]">
                                    Toothrocket is a portfolio company of the <strong className="text-slate-900">Qatar Science & Technology Park</strong> —
                                    the country's premier deep-tech accelerator. QSTP infrastructure, regulatory
                                    fast-tracks, and cross-border IP support underpin every product we push out
                                    of the Doha lab.
                                </p>
                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <div className="rounded-xl bg-white border border-slate-200 p-4">
                                        <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">Location</div>
                                        <div className="mt-1 text-[14.5px] font-medium text-slate-900">Education City · Doha</div>
                                    </div>
                                    <div className="rounded-xl bg-white border border-slate-200 p-4">
                                        <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">Track</div>
                                        <div className="mt-1 text-[14.5px] font-medium text-slate-900">HealthTech · MedDevice</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SIDRA */}
                    <div className="lg:col-span-6">
                        <div className="h-full rounded-[28px] bg-[#0B1120] text-white p-8 lg:p-10 relative overflow-hidden tr-grain">
                            <div className="absolute -top-20 -left-20 w-[280px] h-[280px] tr-glow bg-cyan-500/30" />
                            <div className="relative">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 font-mono-tr text-[10.5px] tracking-[0.22em] uppercase mb-6">
                                    <Rocket size={13} /> · Incoming · 2026
                                </div>
                                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight leading-tight">
                                    Joint incubation with<br />
                                    <span className="italic text-cyan-300/90">Sidra Medicine.</span>
                                </h3>
                                <p className="mt-4 text-white/70 text-[15px] leading-[1.75]">
                                    A signed partnership with Doha's leading research hospital to bring nanotech
                                    <strong className="text-white"> 1-hour 3D-printed surgical guides</strong> to the GCC for
                                    the first time — same-day implantology, fully nano-reinforced, clinically validated.
                                </p>
                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                                        <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-cyan-300">Turnaround</div>
                                        <div className="mt-1 text-[14.5px] font-medium text-white">60 min · print to chair</div>
                                    </div>
                                    <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                                        <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-cyan-300">First in</div>
                                        <div className="mt-1 text-[14.5px] font-medium text-white">GCC Region</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FOUNDER — SYED AMER RUBY VISA */}
            <section className="border-y border-slate-200 bg-gradient-to-b from-slate-50/80 to-white" data-testid="rnd-founder">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-[#0B1120] to-slate-800 aspect-[4/5] flex items-end p-8">
                            <div className="absolute inset-0 opacity-30">
                                <div className="absolute top-10 left-10 w-[240px] h-[240px] tr-glow bg-cyan-400/40" />
                                <div className="absolute bottom-10 right-10 w-[280px] h-[280px] tr-glow bg-rose-400/30" />
                            </div>
                            <div className="relative z-10">
                                <Award size={64} className="text-rose-300 mb-4" strokeWidth={1.25} />
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-300 mb-2">· State of Qatar</div>
                                <div className="font-display font-light text-white text-3xl tracking-tight leading-tight">
                                    The <span className="italic text-rose-300">Ruby Visa.</span>
                                </div>
                                <div className="mt-2 text-white/60 text-[13px] font-mono-tr tracking-wide">Awarded · 2025</div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="tr-overline mb-3">· Founder spotlight</div>
                        <h3 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                            <span className="font-medium">Syed Amer.</span><br />
                            <span className="italic text-slate-500">Honored with Qatar's prestigious Ruby Visa.</span>
                        </h3>
                        <p className="mt-6 text-slate-600 text-[15.5px] leading-[1.85] max-w-2xl">
                            Toothrocket's founder, <strong className="text-slate-900">Syed Amer</strong>, was awarded
                            Qatar's <strong className="text-slate-900">Ruby Visa</strong> — a distinction reserved for
                            exceptional contributors to the nation's strategic sectors — in recognition of his
                            innovative work advancing the healthcare and dental technology sector across Qatar
                            and the wider GCC.
                        </p>
                        <div className="mt-8 grid sm:grid-cols-3 gap-4">
                            <div className="rounded-xl bg-white border border-slate-200 p-5">
                                <Award size={18} className="text-rose-500 mb-2" />
                                <div className="text-[11px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">Distinction</div>
                                <div className="mt-1 font-medium text-slate-900 text-[14px]">Ruby Visa, Qatar</div>
                            </div>
                            <div className="rounded-xl bg-white border border-slate-200 p-5">
                                <Building2 size={18} className="text-cyan-600 mb-2" />
                                <div className="text-[11px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">Sector</div>
                                <div className="mt-1 font-medium text-slate-900 text-[14px]">Healthcare Innovation</div>
                            </div>
                            <div className="rounded-xl bg-white border border-slate-200 p-5">
                                <Layers size={18} className="text-teal-600 mb-2" />
                                <div className="text-[11px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">Footprint</div>
                                <div className="mt-1 font-medium text-slate-900 text-[14px]">Qatar · India · GCC</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTINUOUS DEVELOPMENT NOTE */}
            <section className="max-w-[1080px] mx-auto px-5 lg:px-8 py-20 text-center">
                <div className="tr-overline mb-3">· Continuous development</div>
                <h3 className="font-display font-light text-3xl lg:text-4xl tracking-tight text-slate-900 leading-tight">
                    The landscape of nanotechnology<br />
                    <span className="italic text-slate-500">is constantly evolving — and so are we.</span>
                </h3>
                <p className="mt-5 text-slate-600 text-[15.5px] leading-[1.85] max-w-2xl mx-auto">
                    Our R&D pipeline is continuously fed with new discoveries, ensuring Toothrocket
                    remains the undisputed pioneer in next-generation dental technology.
                </p>
            </section>

            {/* ACTIVE PROJECTS */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-24" data-testid="rnd-projects">
                <div className="mb-14 grid lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-8">
                        <div className="tr-overline mb-3">· In the lab right now</div>
                        <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                            Six active programs.<br />
                            <span className="italic text-slate-500">Serious dentistry, a little playful.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-slate-600 text-[14.5px] leading-[1.8]">
                            Half strictly clinical, half frankly recreational. Both are how we discover
                            what materials can really do.
                        </p>
                    </div>
                </div>
                <div className="space-y-20">
                    {PROJECTS.map((p, i) => {
                        const Icon = p.icon;
                        const reverse = i % 2 === 1;
                        return (
                            <div key={p.title} className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
                                <div className={`lg:col-span-6 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                                    <div className={`rounded-[24px] overflow-hidden border border-slate-200 relative bg-gradient-to-br ${p.accent}`}>
                                        <BrandImage slug={p.slug} fallback={p.img} alt={p.title} className="w-full h-[320px] sm:h-[420px] object-cover mix-blend-multiply" />
                                        <div className="absolute top-4 left-4 px-2.5 py-1 rounded-md bg-white/95 text-[10px] font-mono-tr tracking-[0.2em] uppercase text-slate-900">{p.eyebrow}</div>
                                        {p.launchBadge && (
                                            <div className="absolute top-4 right-4 px-2.5 py-1 rounded-md bg-cyan-600 text-white text-[10px] font-mono-tr tracking-[0.2em] uppercase shadow-lg shadow-cyan-900/20">
                                                Launching · {p.launchBadge}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className={`lg:col-span-6 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                                    <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700 mb-5">
                                        <Icon size={22} />
                                    </div>
                                    <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-[1.05] text-slate-900">{p.title}</h2>
                                    <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.8] max-w-xl">{p.body}</p>
                                    <ul className="mt-6 space-y-2.5">
                                        {p.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-3 text-[14.5px] text-slate-700">
                                                <Sparkles size={15} className="text-cyan-600 mt-0.5 shrink-0" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* PARTNERSHIPS CTA */}
            <section className="border-t border-slate-200 bg-slate-50/60">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="tr-overline mb-3">· Partner with R&D</div>
                        <h3 className="font-display font-light text-4xl tracking-tight text-slate-900 leading-tight">
                            Run a clinical study<br />
                            <span className="italic">or commission a one-off.</span>
                        </h3>
                        <p className="mt-4 text-slate-600 max-w-xl text-[15px] leading-relaxed">
                            We collaborate with dental schools, sports federations, hospitals, and luxury
                            jewellery houses on small-batch and bespoke programs. Reach out — we love the unusual ones.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:flex lg:justify-end gap-3 flex-wrap">
                        <a href="mailto:rnd@toothrocket.com" data-testid="rnd-email-cta" className="tr-btn">Email R&D <ArrowUpRight size={16} /></a>
                        <Link to="/workflow#send-case" className="tr-btn-ghost">Send a case <ArrowRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
